import { useEffect, useId, useMemo, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { PostDeploymentInstructionsView } from "../../components/PostDeploymentInstructionsView";
import { TabPanel } from "../../components/TabPanel";
import type { DeploymentTemplate } from "../../types/pdm.types";
import { useDeploymentTemplateDetails } from "./useDeploymentTemplateDetails";

export function DeploymentTemplateDetailsDialog(props: {
  open: boolean;
  templateId: string | null;
  onClose: () => void;
}) {
  const { open, templateId, onClose } = props;

  const idPrefix = useId();
  const tabDetailsId = `${idPrefix}-template-details-tab`;
  const panelDetailsId = `${idPrefix}-template-details-panel`;
  const tabPostId = `${idPrefix}-template-post-tab`;
  const panelPostId = `${idPrefix}-template-post-panel`;

  const [tab, setTab] = useState(0);

  const detailsQuery = useDeploymentTemplateDetails(templateId, open);
  const template = detailsQuery.data ?? null;

  const postInstructionsRaw = template?.postDeploymentInstructions;
  const postInstructions = Array.isArray(postInstructionsRaw)
    ? postInstructionsRaw
    : undefined;
  const showPostTab =
    detailsQuery.isSuccess &&
    postInstructionsRaw !== undefined &&
    postInstructionsRaw !== null &&
    Boolean(postInstructions?.length);

  useEffect(() => {
    setTab(0);
  }, [templateId, open]);

  useEffect(() => {
    if (tab === 1 && !showPostTab) {
      setTab(0);
    }
  }, [tab, showPostTab]);

  const createdAtLabel = useMemo(
    () => formatMaybeDate(template?.createdAt),
    [template?.createdAt],
  );
  const updatedAtLabel = useMemo(
    () => formatMaybeDate(template?.updatedAt),
    [template?.updatedAt],
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Template details
          </Typography>
          <IconButton aria-label="close" edge="end" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {detailsQuery.isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {detailsQuery.isError && (
          <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto" }}>
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void detailsQuery.refetch()}
                >
                  Retry
                </Button>
              }
            >
              Unable to load deployment template details.
            </Alert>
          </Stack>
        )}

        {template && !detailsQuery.isLoading && !detailsQuery.isError && (
          <Stack spacing={2} sx={{ maxWidth: 1100, mx: "auto" }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Tabs
                id={`${idPrefix}-template-section-tabs`}
                value={tab}
                onChange={(_event, newValue) => {
                  setTab(typeof newValue === "number" ? newValue : 0);
                }}
                aria-label="Deployment template sections"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ minHeight: 48 }}
              >
                <Tab
                  id={tabDetailsId}
                  aria-controls={panelDetailsId}
                  label="Details / schema"
                />
                {showPostTab ? (
                  <Tab
                    id={tabPostId}
                    aria-controls={panelPostId}
                    label="Post deployment instructions"
                  />
                ) : null}
              </Tabs>
            </Box>

            <TabPanel
              value={tab}
              index={0}
              id={panelDetailsId}
              labelledBy={tabDetailsId}
            >
              <Stack spacing={2.5}>
                <Stack spacing={0.5}>
                  <Typography variant="h5" sx={{ wordBreak: "break-word" }}>
                    {template.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {template.id}
                  </Typography>
                </Stack>

                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">General information</Typography>
                  <Divider />
                  <Grid container spacing={2}>
                    <Field
                      label="Contract name"
                      value={template.contractName ?? "—"}
                      mono
                    />
                    <Field label="Deployment task" value={template.deploymentTask} />
                    <Field label="ds-App ID" value={template.protocolId} mono />
                    <Field
                      label="Description"
                      value={template.description || "—"}
                    />
                    <Field label="Created at" value={createdAtLabel} />
                    <Field label="Updated at" value={updatedAtLabel} />
                  </Grid>
                </Stack>

                <InitializationSchemaBlock schema={template.initializationSchema} />
              </Stack>
            </TabPanel>

            {showPostTab ? (
              <TabPanel
                value={tab}
                index={1}
                id={panelPostId}
                labelledBy={tabPostId}
              >
                {tab === 1 && postInstructions ? (
                  <PostDeploymentInstructionsView instructions={postInstructions} />
                ) : null}
              </TabPanel>
            ) : null}
          </Stack>
        )}
      </Box>
    </Dialog>
  );
}

function InitializationSchemaBlock(props: {
  schema: DeploymentTemplate["initializationSchema"];
}) {
  const { schema } = props;
  const code = useMemo(() => JSON.stringify(schema ?? {}, null, 2), [schema]);

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle1">Initialization schema</Typography>
      <Divider />
      <Stack spacing={0.75}>
        <Typography variant="caption" color="text.secondary">
          JSON schema
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            overflow: "hidden",
            bgcolor: (t) =>
              t.palette.mode === "dark" ? "grey.900" : t.palette.action.hover,
          }}
        >
          <Box
            sx={{
              maxHeight: 420,
              overflow: "auto",
              tabIndex: 0,
            }}
          >
            <Box
              component="pre"
              sx={(theme) => ({
                m: 0,
                p: 2,
                fontSize: theme.typography.body2.fontSize,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                color: "text.primary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              })}
            >
              {code}
            </Box>
          </Box>
        </Paper>
      </Stack>
    </Stack>
  );
}

function Field(props: { label: string; value: string; mono?: boolean }) {
  const { label, value, mono } = props;
  return (
    <Grid item xs={12} md={6}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            wordBreak: mono ? "break-all" : "break-word",
            fontFamily: mono
              ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
              : undefined,
          }}
        >
          {value || "—"}
        </Typography>
      </Stack>
    </Grid>
  );
}

function formatMaybeDate(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
